import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface BrandIntroProps {
    enabled?: boolean;
    logoUrl?: string;
}

export const BrandIntro: React.FC<BrandIntroProps> = ({
    enabled = true,
    logoUrl = "/Rakib's logo.jpg"
}) => {
    const [visible, setVisible] = useState<boolean>(false);
    const overlayRef = useRef<HTMLDivElement>(null);
    const logoContainerRef = useRef<HTMLDivElement>(null);
    const brandNameRef = useRef<HTMLHeadingElement>(null);
    const taglineRef = useRef<HTMLParagraphElement>(null);
    const progressLineRef = useRef<HTMLDivElement>(null);
    const badgeRef = useRef<HTMLDivElement>(null);
    const tlRef = useRef<gsap.core.Timeline | null>(null);

    useEffect(() => {
        if (!enabled) return;

        // Respect reduced motion
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        // Check session storage
        const hasSeenIntro = sessionStorage.getItem('rakib_care_intro_seen');
        if (hasSeenIntro) return;

        setVisible(true);
    }, [enabled]);

    useEffect(() => {
        if (!visible) return;

        const overlay = overlayRef.current;
        const logo = logoContainerRef.current;
        const brandName = brandNameRef.current;
        const tagline = taglineRef.current;
        const progressLine = progressLineRef.current;
        const badge = badgeRef.current;

        if (!overlay || !logo || !brandName || !tagline || !progressLine) return;

        const dismissIntro = () => {
            sessionStorage.setItem('rakib_care_intro_seen', 'true');
            gsap.to(overlay, {
                opacity: 0,
                scale: 1.05,
                duration: 0.7,
                ease: 'power3.inOut',
                onComplete: () => {
                    setVisible(false);
                }
            });
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                dismissIntro();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        // Failsafe timer (max 4 seconds)
        const failsafe = setTimeout(() => {
            dismissIntro();
        }, 4000);

        const tl = gsap.timeline({
            onComplete: () => {
                clearTimeout(failsafe);
                dismissIntro();
            }
        });
        tlRef.current = tl;

        tl.set(overlay, { opacity: 1 })
          .fromTo(logo, 
              { scale: 0.5, opacity: 0, filter: 'blur(16px)' }, 
              { scale: 1, opacity: 1, filter: 'blur(0px)', duration: 1.1, ease: 'power3.out' }
          )
          .fromTo(badge,
              { y: -10, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
              '-=0.5'
          )
          .fromTo(brandName,
              { y: 25, opacity: 0, filter: 'blur(6px)' },
              { y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.8, ease: 'power3.out' },
              '-=0.4'
          )
          .fromTo(tagline,
              { y: 15, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
              '-=0.4'
          )
          .fromTo(progressLine,
              { scaleX: 0 },
              { scaleX: 1, duration: 1.0, ease: 'power1.inOut' },
              '-=0.5'
          )
          .to({}, { duration: 0.6 });

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            clearTimeout(failsafe);
            if (tlRef.current) tlRef.current.kill();
        };
    }, [visible]);

    if (!visible) return null;

    const handleSkip = () => {
        sessionStorage.setItem('rakib_care_intro_seen', 'true');
        if (overlayRef.current) {
            gsap.to(overlayRef.current, {
                opacity: 0,
                duration: 0.4,
                ease: 'power2.inOut',
                onComplete: () => setVisible(false)
            });
        } else {
            setVisible(false);
        }
    };

    return (
        <div 
            ref={overlayRef}
            id="brand-intro-overlay" 
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950 text-white overflow-hidden select-none" 
            aria-hidden="true"
        >
            {/* Ambient Background Aura Lights */}
            <div className="absolute w-[500px] h-[500px] rounded-full bg-emerald-500/15 filter blur-[120px] pointer-events-none animate-pulse-aura" />
            <div className="absolute w-[350px] h-[350px] rounded-full bg-teal-500/10 filter blur-[90px] pointer-events-none" />
            <div className="absolute inset-0 bg-grid-pattern opacity-15 pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center px-6">
                
                {/* Protocol Badge */}
                <div ref={badgeRef} className="mb-6 inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-slate-900/90 border border-emerald-500/30 text-[10px] font-mono uppercase tracking-widest text-emerald-400 shadow-xl shadow-emerald-950/50">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span>Official Identity // Rakib Hasan</span>
                </div>

                {/* Rotating Cybernetic Emblem */}
                <div 
                    ref={logoContainerRef}
                    id="intro-logo" 
                    className="relative w-36 h-36 md:w-48 md:h-48 flex items-center justify-center mb-6"
                >
                    {/* Outer Rotating Ring */}
                    <svg className="absolute inset-0 w-full h-full animate-spin-slow text-emerald-500/40" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 6" />
                        <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(16, 185, 129, 0.8)" strokeWidth="2" strokeDasharray="16 84" />
                    </svg>

                    {/* Inner Rotating Ring */}
                    <svg className="absolute inset-2 w-[calc(100%-16px)] h-[calc(100%-16px)] animate-spin-reverse-slow text-teal-400/50" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="6 14" />
                        <polygon points="50,4 53,10 47,10" fill="currentColor" />
                        <polygon points="50,96 53,90 47,90" fill="currentColor" />
                    </svg>

                    {/* Central Glowing Crest */}
                    <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden p-1 shadow-[0_0_35px_rgba(16,185,129,0.5)] ring-2 ring-emerald-500/80 bg-slate-900 z-10">
                        <img src={logoUrl} alt="Rakib's Care Logo" className="w-full h-full object-cover rounded-full" />
                    </div>
                </div>

                {/* Brand Title */}
                <h1 
                    ref={brandNameRef}
                    id="intro-brand-name" 
                    className="text-4xl md:text-6xl font-extrabold tracking-tight text-white uppercase font-sans mb-2"
                >
                    Rakib's <span className="text-emerald-400 drop-shadow-[0_0_20px_rgba(16,185,129,0.5)]">Care</span>
                </h1>
                
                {/* Brand Tagline */}
                <p 
                    ref={taglineRef}
                    id="intro-tagline" 
                    className="text-slate-300 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase max-w-md"
                >
                    Leadership • Purpose • Impact
                </p>

                {/* Glowing Progress Line */}
                <div className="w-56 h-1 bg-slate-800/90 rounded-full mt-8 overflow-hidden relative shadow-inner">
                    <div 
                        ref={progressLineRef}
                        id="intro-progress-line" 
                        className="w-full h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-300 origin-left shadow-[0_0_12px_#10b981]"
                    ></div>
                </div>

                {/* Skip Button */}
                <button 
                    id="intro-skip-btn" 
                    type="button" 
                    onClick={handleSkip}
                    className="mt-8 px-5 py-2 text-xs tracking-widest uppercase font-mono text-slate-400 hover:text-emerald-300 border border-slate-800 hover:border-emerald-500/50 bg-slate-900/60 rounded-full transition cursor-pointer backdrop-blur-xs"
                    aria-label="Skip introductory animation"
                >
                    Skip Intro &rarr;
                </button>
            </div>
        </div>
    );
};
